import { isEmpty } from "lodash-es";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon";
import { diffUntilNow } from "@/core/dates";
import { IJobRequest } from "@/data/job-requests";
import { formatMultipleLocations } from "@/data/locations";
import { Col, Flex, Row, Tooltip } from "@/eworkitComponents/components";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";
import { JobRequestDetailSection } from "@/views/JobRequests/Detail/components/JobRequestDetailSection";
import { JobRequestExpandableDetailSection } from "@/views/JobRequests/Detail/components/JobRequestExpandableDetailSection";

interface IJobRequestDetailSectionsProps {
  payload: IJobRequest;
}

const BASE_NUMBER_OF_LOCATIONS_SHOWN = 2;

export const JobRequestDetailSections: FC<IJobRequestDetailSectionsProps> = ({ payload }) => {
  const { t } = useTranslation("session");
  const codelistTranslation = useCodelistTranslation();
  const { formatDate, formatDateTime } = useFormats();

  const locationEmpty = isEmpty(payload.locations);
  const daysLeft = diffUntilNow(payload.lastDayOfApplications, "days");
  const daysLeftString = daysLeft > 0 ? t("session:JOB_REQUESTS.DETAIL.DAYS_LEFT", { count: daysLeft }) : "";

  return (
    <Flex direction="column">
      <Row>
        {!!payload.skillRole && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.SKILL_ROLE")}
              value={payload.skillRole.name}
              grow={0}
            />
          </Col>
        )}

        {!!payload.level && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.SENIORITY_LEVEL")}
              value={codelistTranslation("skill-role-levels", payload.level)}
              grow={0}
            />
          </Col>
        )}

        {!locationEmpty && payload.locations?.length && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestExpandableDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.LOCATION", { count: payload.locations.length })}
              shortValue={formatMultipleLocations(t, payload.locations, BASE_NUMBER_OF_LOCATIONS_SHOWN)}
              longValue={formatMultipleLocations(t, payload.locations, payload.locations?.length)}
              buttonLabel={t("session:JOB_REQUESTS.DETAIL.LOCATION_SHOW_MORE", {
                value: payload.locations!.length - BASE_NUMBER_OF_LOCATIONS_SHOWN,
              })}
            />
          </Col>
        )}

        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailSection
            label={t("session:JOB_REQUESTS.DETAIL.REMOTENESS.REMOTE")}
            value={`${payload.remoteness || 0}%`}
          />
        </Col>

        {payload.startDate && payload.endDate && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.ASSIGNMENT_PERIOD")}
              value={
                <Flex direction="row" innerMargin="xs">
                  {formatDate(payload.startDate)}
                  <Icon icon="ic_arrow_forward" color="grey2" />
                  {formatDate(payload.endDate)}
                </Flex>
              }
            />
          </Col>
        )}

        {payload.lastDayOfApplications && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.LAST_DAY_OF_APPLICATION")}
              value={
                <Tooltip placement="top" content={t("JOB_REQUESTS.DETAIL.DEADLINE_WARNING")} key="dates">
                  <Flex>
                    {payload.numberOfPositions && payload.numberOfPositions > 0 && (
                      <Icon icon="warning" size="md" color="warning" />
                    )}
                    {`${formatDateTime(payload.lastDayOfApplications)} ${daysLeftString}`}
                  </Flex>
                </Tooltip>
              }
            />
          </Col>
        )}
      </Row>
    </Flex>
  );
};
